import { Capacitor } from '@capacitor/core';

export type ProfileEnv = 'local' | 'dev' | 'prod';

export interface MobileAppConfig {
  env: ProfileEnv;
}

declare global {
  interface Window {
    __APP_CONFIG__?: MobileAppConfig & Record<string, unknown>;
  }
}

/**
 * Retrieves the embedded mobile application configuration.
 * Prioritizes window.__APP_CONFIG__ (injected into public/app-config.js at binary sync time).
 * Falls back to provided fallback or a default local profile configuration.
 */
export function getAppConfig<T extends MobileAppConfig = MobileAppConfig>(fallback?: T): T {
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    const cfg = window.__APP_CONFIG__;
    const env = (cfg.env as ProfileEnv) || (cfg.environment as ProfileEnv) || fallback?.env || 'local';
    return {
      ...(fallback || {}),
      ...cfg,
      env,
    } as T;
  }

  return {
    ...(fallback || {}),
    env: fallback?.env || 'local',
  } as T;
}

/**
 * Resolves the operational API URL for the current platform:
 * 1. Honors localStorage override if present.
 * 2. On Android emulators, transforms 'localhost' or '127.0.0.1' to '10.0.2.2'.
 * 3. Returns the resulting target URL.
 */
export function resolveApiUrl(config: { apiUrl?: string }, overrideKey = 'api_url_override'): string {
  if (typeof localStorage !== 'undefined') {
    const override = localStorage.getItem(overrideKey);
    if (override && override.trim() !== '') {
      return override.trim();
    }
  }

  const url = config.apiUrl || '';
  if (!url) {
    return '';
  }

  const platform = Capacitor.getPlatform();
  if (platform === 'android') {
    return url
      .replace('://localhost', '://10.0.2.2')
      .replace('://127.0.0.1', '://10.0.2.2');
  }

  return url;
}
